<script>
    import field from './ib-field.vue';
    import $device from '../../global/device';
    import $locale from '../../global/locale';
    import $num from '../../global/num';

    const haveInputMode = $device.keyboard || 'inputMode' in HTMLInputElement.prototype;
    const zwnj = '\u200c';

    const decimalPlaces = value => {
        value = '' + +value;
        const eindex = value.indexOf('e');
        const dindex = value.indexOf('.');
        return (
            (dindex < 0 ? 0 : (eindex < 0 ? value.length : eindex) - dindex - 1) +
            (eindex < 0 ? 0 : Math.max(0, -parseInt(value.slice(eindex + 1)))));
    }


    /**
     * A derivative of [ib-field](#/components/form/ib-field) that restricts input to numbers, decimals, or signs,
     * and emits `Number` values. Mobile devices will display a numeric (or alphanumeric) keyboard on focus. Any
     * invalid input state will be emitted as `NaN`.
     *
     * Input is limited to a maximum of about 16 digits on both sides of the decimal point. This is a limitation of
     * JavaScript numbers. If that is not adequate, you will need to use [ib-field](#/components/form/ib-field) with
     * `inputmode="decimal"`, which will emit `String` values.
     *
     * While it is possible to use the `maxlength` prop with this component, it is not recommended. In right to left
     * reading direction, the browser may inject Unicode direction characters unexpectedly, and these are counted
     * against the maximum length.
     */
    export default {
        name: 'ib-field-numeric',
        mixins: [field],

        props: {
            /**
             * Current value.
             */
            value: {
                type: Number,
                default: undefined
            },
            /**
             * Minimum legal value.
             */
            min: {
                type: Number,
                default: undefined
            },
            /**
             * Maximum legal value.
             */
            max: {
                type: Number,
                default: undefined
            },
            /**
             * Stepping interval for legal values. If defined with a decimal value, decimal entry will be allowed on the
             * field with enough decimal places to accommodate `step`.
             */
            step: {
                type: Number,
                default: undefined,
                validator: step => step == null || (step >= 1e-8 && step <= 1e8)
            },
            /**
             * If `true`, allow decimal entry. If defined without `step`, no limit on decimal places will be imposed.
             */
            decimal: Boolean,
            /**
             * If `true`, always display enough decimal places to accommodate `step`, regardless of the value entered by
             * the user. For example, if `step` is `0.01` and the user enters `1.5`, the displayed value will be `1.50`.
             * This is typically used with `autoselect`.
             */
            fixed: Boolean,
            /**
             * If `true`, automatically insert thousands/lakh/crore separators.
             */
            useGrouping: Boolean,
            /**
             * Formatting mode; `percent` and `ticks` are supported. `percent` format means values are formatted and
             * parsed as percentages; a percent symbol will be displayed in the locale appropriate position and the
             * displayed value will be 100 times the stored value. For example, entering `5` will emit `0.05` and will
             * fail a test of `:step="1"`. `ticks` means values are formatted and parsed as ticks (referred to
             * internally as Garban pricing) with three places right of apostrophe separator.
             */
            format: String,
            /**
             * If `true`, emit the value entered in the field on input even if it fails `min`, `max`, or `step`
             * validation.
             */
            raw: Boolean,
            /**
             * Writing system. Only `latn` is currently supported.
             */
            script: {
                type: String,
                default: 'latn'
            },
            /**
             * Custom error string for value too low
             */
            errorUnderflow: String,
            /**
             * Custom error string for value too high
             */
            errorOverflow: String
        },

        computed: {
            _iclass() {
                return {
                    numeric: true
                };
            },

            /** Minimum allowed number for display, noninclusive; clamped to safe digits for JS arithmetic */
            amin() {
                const min = Number.MIN_SAFE_INTEGER / (this.pfactor || 1);
                return -(10 ** (Math.log10(Math.abs(this.min > min ? this.min : min)) + 1 | 0));
            },

            /** Maximum allowed number for display, noninclusive; clamped to safe digits for JS arithmetic */
            amax() {
                const max = Number.MAX_SAFE_INTEGER / (this.pfactor || 1);
                return 10 ** (Math.log10(Math.abs(this.max < max ? this.max : max)) + 1 | 0);
            },

            /** Generate a function that returns a truthy result if the current value is *not* a multiple of `step` */
            stepInvalid() {
                const pfactor = this.pfactor;
                return pfactor
                    ? value => {
                        return 10 ** decimalPlaces(value) > pfactor
                            ? true
                            : Math.round(pfactor * value) % this.pstep !== 0;
                    }
                    : value => false;
            },

            bind() {
                return {
                    type: 'text',
                    inputmode: !haveInputMode || ($device.ios && !(this.min >= 0)) ? null : this.decimal || this.fplaces ? 'decimal' : 'numeric',
                    autocomplete: 'off',
                    ...this.$attrs,
                    'class': void 0,
                    'style': void 0
                };
            },

            /** Convert stored value to displayed value */
            formatter() {
                const format = this.format;
                const step = this.step;
                return format == 'ticks'
                    ? $num.ticks
                    : $num.formatter({
                        numberingSystem: this.script,
                        useGrouping: !!this.useGrouping,
                        minimumFractionDigits: this.fixed ? this.fplaces : 0,
                        maximumFractionDigits: 20,
                        style: format == 'percent'
                            ? format
                            : undefined
                    });
            },

            /** Convert displayed value to stored value */
            parser() {
                const format = this.format;
                const step = this.step;
                return (
                    format == 'ticks'
                        ? $num.parseTicks
                        : format == 'percent'
                            ? $num.parsePercent
                            : $num.parse
                );
            },

            fdata() {
                return this.format == 'ticks' ? {
                    decimal: "'",
                    autodec: !$device.keyboard,
                    autopad: true
                } : {
                    decimal: $num.data.decimal,
                    autodec: $num.data.decimal != '.',
                    autopad: this.fixed
                };
            },

            toDisplay() {
                return value => isNaN(value) ? null : this.formatter(value).replace(/^[^\d-]+|\D+$/g, '');
            },

            /** Precision factor; for converting numeric operations to integer operations */
            pfactor() {
                const fplaces = this.fplaces;
                if (fplaces != null) {
                    return 10 ** (fplaces + (this.format == 'percent' ? 2 : 0));
                }
            },

            /** Step converted to integer */
            pstep() {
                return Math.round(this.pfactor * this.step);
            },

            /** Maximum fractional (mantissa) decimal places */
            fplaces() {
                const format = this.format;
                const step = this.step;
                if (step > 0) {
                    return decimalPlaces(step) - (format == 'percent' ? 2 : 0);
                }
            },

            _prefix() {
                return this.format == 'percent' && this.$rtl ? $num.data.percentSign : this.prefix != null && this.prefix != '' ? this.prefix : zwnj;
            },

            _suffix() {
                return this.format == 'percent' && !this.$rtl ? $num.data.percentSign : null;
            }
        },

        watch: {
            min: 'input',
            max: 'input',
            step: 'input',
            formatter: 'leave',
            format: {
                handler(format) {
                    if (format == 'ticks') {
                        const step = this.step;
                        if (step != 1/32 && step != 1/64 && step != 1/128 && step != 1/256) {
                            console.error('invalid step: ' + step);
                        }
                    }
                },
                immediate: true
            }
        },

        mounted() {
            // Display alphanumeric keyboard on devices that do not support inputmode
            // or on iOS when negative numbers are needed
            if (!haveInputMode || ($device.ios && !(this.min >= 0))) {
                const el = this.$refs._in;
                el.addEventListener('touchstart', () => {
                    if (document.activeElement !== el && el.getAttribute('type') == 'text') {
                        this.saveState();
                        el.setAttribute('type', 'number');
                    }
                });
                el.addEventListener('focus', () => {
                    if (el.getAttribute('type') != 'text') {
                        el.setAttribute('type', 'text');
                        this.restoreState();
                    }
                });
            }
            this.saveState();
        },

        methods: {
            input() {
                const input = this.$refs._in;
                const deleted = this.which === 8 || this.which === 46;
                const decimal = this.fdata.decimal;
                const autopad = this.fdata.autopad;

                let value = input.value;
                let selectionStart = input.selectionStart;
                let selectionEnd = input.selectionEnd;

                // Replace English decimal separator with local separator in just inserted text
                if (this.fdata.autodec) {
                    const dindex = value.indexOf('.', this.saveStart);
                    if (dindex >= 0 && dindex < selectionStart) {
                        value = value.slice(0, dindex) + decimal + value.slice(dindex + 1);
                    }
                }

                // Special delete handling for fixed
                if (deleted && autopad) {
                    if (value.charAt(0) === decimal) {
                        value = 0 + value;
                        selectionStart = selectionEnd = 1;
                    } else if (value && !value.includes(decimal)) {
                        const saveValue = this.saveValue;
                        if (saveValue && saveValue.includes(decimal)) {
                            value = saveValue;
                        }
                    }
                }

                let nvalue = this.parser(value);
                if (isNaN(nvalue)) {
                    if (value == decimal) {
                        value = 0 + decimal;
                        selectionStart++;
                        selectionEnd++;
                        nvalue = 0;
                    } else if (value == '-' + decimal) {
                        value = '-0' + decimal;
                        selectionStart++;
                        selectionEnd++;
                        nvalue = -0;
                    } else {
                        // Remove double decimal in just inserted text
                        const dindex = value.indexOf(decimal, this.saveStart - (selectionEnd === value.length));
                        if (dindex >= 0 && value.charAt(dindex + 1) === decimal) {
                            value = value.slice(0, dindex) + value.slice(dindex + 1);
                            nvalue = this.parser(value);
                        }
                    }
                }
                if ((isNaN(nvalue) && !((value == '-' || value == '-.') && !(this.min >= 0))) || (nvalue <= this.amin || nvalue >= this.amax)) {
                    this._warn();
                    this.restoreState();
                } else {
                    let svalue = value ? this.toDisplay(nvalue) : value;
                    if (svalue != null) {
                        // preserve decimal point and zeros not generated by formatter but present in focused field input
                        if (this.focused) {
                            let dindex = svalue.indexOf(decimal);
                            if (dindex < 0 && value.includes(decimal)) {
                                dindex = svalue.length;
                                svalue += decimal;
                            }
                            if (dindex < 0) {
                                if (!deleted && this.fplaces && selectionEnd === value.length) {
                                    // Auto-insert separator
                                    if ((nvalue > 0 && nvalue <= this.max && nvalue * 10 > this.max) ||
                                        (nvalue < 0 && nvalue >= this.min && nvalue * 10 < this.min)) {
                                        svalue += decimal;
                                        selectionStart++;
                                        selectionEnd++;
                                    }
                                }
                            } else {
                                if (!autopad) {
                                    svalue += value.match(/0*$/)[0];
                                }
                                if (dindex < svalue.length - (this.fplaces ? this.fplaces + 1 : 0) && !this.decimal) {
                                    this._warn();
                                    this.restoreState();
                                    return;
                                }
                            }
                        }
                        if (input.value !== svalue) {
                            // Formatter generated some different display text than what is entered; replace and maintain cursor
                            input.value = svalue;
                            if (svalue.startsWith(value.slice(0, selectionStart))) {
                                input.selectionStart = input.selectionEnd = selectionStart;
                            } else if (svalue.endsWith(value.slice(selectionEnd))) {
                                input.selectionStart = input.selectionEnd = selectionEnd + svalue.length - value.length;
                            }
                        }
                    }

                    this.hcaret = false;
                    this.empty = !input.value;
                    this.which = 0;
                    this.validate();
                    this._emit(this.toValue());
                    this.saveState();
                }
            },

            /** @private */
            leave() {
                const value = this.ivalue;
                if (value != null) {
                    let svalue = this.toDisplay(value);
                    if (svalue != null) {
                        const input = this.$refs._in;
                        if (!this.fixed && svalue.includes(this.separator)) {
                            svalue += input.value.match(/0*$/)[0];
                        }
                        if (input.value !== svalue) {
                            input.value = svalue;
                        }
                    }
                }
                return value;
            },

            /**
             * Increase the value to the next higher amount allowed by `step`. If `step` is undefined, do nothing.
             */
            increment() {
                const pfactor = this.pfactor;
                if (pfactor) {
                    let value = (Math.round(pfactor * this.parser(this.$refs._in.value)) + this.pstep) / pfactor;
                    if (value > this.max) {
                        value = this.max;
                    }
                    this._set(value);
                }
            },

            /**
             * Decrease the value to the next lower amount allowed by `step`. If `step` is undefined, do nothing.
             */
            decrement() {
                const pfactor = this.pfactor;
                if (pfactor) {
                    let value = (Math.round(pfactor * this.parser(this.$refs._in.value)) - this.pstep) / pfactor;
                    if (value < this.min) {
                        value = this.min;
                    }
                    this._set(value);
                }
            },

            /** @private */
            drop(e) {
                e.preventDefault();
            },

            /** @private */
            keydown(e) {
                const which = e.which;
                this.saveState();
                this.which = which;
                if (which == 38) {
                    this.increment();
                    e.preventDefault();
                } else if (which == 40) {
                    this.decrement();
                    e.preventDefault();
                }
                this.$emit('keydown', e);
            },

            /** @private */
            paste(e) {
                this.saveState();
                this.which = 0;
                this.$emit('paste', e);
            },

            /** @private */
            change() {
                this.$emit('change', this.ivalue);
            },

            /**
             * Convert edited/displayed value to `this.value`.
             * @private
             */
            toValue() {
                const input = this.$refs._in;
                return input.value
                    ? this.valid || this.raw
                        ? this.parser(input.value)
                        : NaN
                    : null;
            },

            /**
             * Custom validation, due to this component using `<input type="text">` which does not support min/max/step
             * validation in HTML5.
             * @private
             */
            validate() {
                let validity = null;
                const input = this.$refs._in;
                if (this.error) {
                    validity = this.error;
                } else if (!input.value) {
                    if (this.required) {
                        validity = (this.errorMissing || $locale.data.numMissing);
                    }
                } else {
                    const value = this.parser(input.value);
                    if (isNaN(value)) {
                        validity = (this.errorInvalid || $locale.data.numInvalid);
                    } else if (value < this.min) {
                        validity = (this.errorUnderflow || $locale.data.numMin).replace('{min}', this.formatter(this.min));
                    } else if (value > this.max) {
                        validity = (this.errorOverflow || $locale.data.numMax).replace('{max}', this.formatter(this.max));
                    } else if (this.stepInvalid(value)) {
                        validity = (this.errorInvalid || $locale.data.numStep).replace('{step}', this.formatter(this.step));
                    }
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return !validity;
            },

            getvalidity() {
                return this.validity;
            }
        }
    }
</script>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(v-model="value", id="default")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="grouping" name="Grouping">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    v-model="value",
                    use-grouping,
                    :step=".01",
                    id="default")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="fixed" name="Fixed">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    v-model="value",
                    fixed,
                    autoselect,
                    :step=".01",
                    id="default")
    </template>
    <script>
        {
            data() {
                return {
                    value: 0
                };
            }
        }
    </script>
</ib-example>

<ib-example id="fixed-grouping" name="Fixed/Grouping/Max">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    v-model="value",
                    fixed,
                    use-grouping,
                    autoselect,
                    :step=".01",
                    :max="10000",
                    id="default")
    </template>
    <script>
        {
            data() {
                return {
                    value: 0
                };
            }
        }
    </script>
</ib-example>

<ib-example id="minmaxstep" name="Min/Max/Step">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    v-model="value",
                    id="min",
                    :min="10",
                    placeholder=":min=10",
                    immediate)
            .before-4
                ib-field-numeric(
                    v-model="value",
                    id="max",
                    :max="20",
                    :step=".01",
                    placeholder=":max=20 :step=.01",
                    immediate)
            .before-4
                ib-field-numeric(
                    v-model="value",
                    id="step",
                    :min="0",
                    :max="100",
                    :step=".05",
                    placeholder=":min=0 :max=100 :step=.05",
                    immediate)
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="increment" name="Increment/Decrement">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    ref="field",
                    v-model="value",
                    id="step",
                    :min="0",
                    :max="1",
                    :step=".00001",
                    immediate)
                ib-button(@click="decrement") -
                ib-button(@click="increment") +
    </template>
    <script>
        {
            data() {
                return {
                    value: .5
                };
            },

            methods: {
                decrement() {
                    this.$refs.field.decrement();
                },
                increment() {
                    this.$refs.field.increment();
                }
            }
        }
    </script>
</ib-example>

<ib-example id="percent" name="Percent">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    v-model="value",
                    id="default",
                    format="percent")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="ticks" name="1/128 Ticks (Garban Pricing)">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field-numeric(
                    v-model="value",
                    id="default",
                    format="ticks",
                    :step="1/128",
                    immediate)
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>
